import { Item } from "@forgepoint/shared";
import { getUnitCostForQuantity } from "@/modules/items/utils";
import { formatCurrency } from "@/lib/utils";
import { useOrganizationStore } from "@/store/organizationStore";

const SINGLE_UNIT_QUANTITY = 1;

interface ItemRowPriceProps {
  /** The item whose unit cost is displayed. */
  item: Item;
  /** "row" stacks the label under the amount, "trigger" keeps it on one line. */
  variant: "row" | "trigger";
}

/**
 * Renders an item's unit cost at quantity 1, or nothing when no cost break applies.
 * @param props - The item and the layout variant
 * @returns The formatted cost labelled "cost /ea", or null when there is no cost to show
 */
export function ItemRowPrice({ item, variant }: ItemRowPriceProps) {
  // List and get responses do not stamp a currency, so fall back to the base
  // currency the item's costs are denominated in.
  const baseCurrency = useOrganizationStore(
    (state) => state.organization?.baseCurrency,
  );
  const unitCost = getUnitCostForQuantity(item, SINGLE_UNIT_QUANTITY);
  if (unitCost === null) {
    return null;
  }

  const amount = formatCurrency(unitCost, item.currency ?? baseCurrency);

  if (variant === "trigger") {
    return (
      <span className="tabular-nums">
        {amount}
        <span className="text-muted-foreground"> cost /ea</span>
      </span>
    );
  }

  return (
    <div className="flex flex-col items-end leading-tight tabular-nums">
      <span>{amount}</span>
      <span className="text-xs text-muted-foreground">cost /ea</span>
    </div>
  );
}
